/**
 * Tooltip Component
 *
 * Simple tooltip using CSS for accessibility.
 * Shows on hover/focus with optional positioning.
 */

import { ReactNode, useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface TooltipProps {
    /** The element that triggers the tooltip */
    children: ReactNode;
    /** Tooltip content */
    content: ReactNode;
    /** Position of tooltip relative to trigger */
    position?: 'top' | 'bottom' | 'left' | 'right';
    /** Additional class names for the tooltip container */
    className?: string;
    /** Max width of tooltip */
    maxWidth?: string;
}

export const Tooltip = ({
    children,
    content,
    position = 'top',
    className,
    maxWidth = '250px',
}: TooltipProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isMobileViewport, setIsMobileViewport] = useState(false);
    const preferredPosition = isMobileViewport && position === 'top' ? 'bottom' : position;
    const [adjustedPosition, setAdjustedPosition] = useState(preferredPosition);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 640px)');

        const updateViewport = (event: MediaQueryList | MediaQueryListEvent) => {
            setIsMobileViewport(event.matches);
        };

        updateViewport(mediaQuery);
        mediaQuery.addEventListener('change', updateViewport);

        return () => {
            mediaQuery.removeEventListener('change', updateViewport);
        };
    }, []);

    useEffect(() => {
        setAdjustedPosition(preferredPosition);
    }, [preferredPosition]);

    // Adjust position if tooltip would go off-screen
    useEffect(() => {
        if (isVisible && tooltipRef.current && triggerRef.current) {
            const tooltipRect = tooltipRef.current.getBoundingClientRect();

            let newPosition = preferredPosition;

            // Check if tooltip goes off screen and adjust
            if (preferredPosition === 'top' && tooltipRect.top < 0) {
                newPosition = 'bottom';
            } else if (preferredPosition === 'bottom' && tooltipRect.bottom > window.innerHeight) {
                newPosition = 'top';
            } else if (preferredPosition === 'left' && tooltipRect.left < 0) {
                newPosition = 'right';
            } else if (preferredPosition === 'right' && tooltipRect.right > window.innerWidth) {
                newPosition = 'left';
            }

            if (newPosition !== adjustedPosition) {
                setAdjustedPosition(newPosition);
            }
        }
    }, [isVisible, preferredPosition, adjustedPosition]);

    const positionClasses = {
        top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
        bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
        left: 'right-full top-1/2 -translate-y-1/2 mr-2',
        right: 'left-full top-1/2 -translate-y-1/2 ml-2',
    };

    const arrowClasses = {
        top: 'top-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-foreground',
        bottom: 'bottom-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-foreground',
        left: 'left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-foreground',
        right: 'right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-foreground',
    };

    const maxWidthClasses: Record<string, string> = {
        '250px': 'max-w-[250px]',
        '320px': 'max-w-[320px]',
        '420px': 'max-w-[420px]',
    };
    const maxWidthClass = maxWidthClasses[maxWidth] ?? maxWidthClasses['250px'];

    return (
        <div
            ref={triggerRef}
            className={cn('relative inline-block', className)}
            onMouseEnter={() => setIsVisible(true)}
            onMouseLeave={() => setIsVisible(false)}
            onFocus={() => setIsVisible(true)}
            onBlur={() => setIsVisible(false)}
        >
            {children}
            {isVisible && (
                <div
                    ref={tooltipRef}
                    role="tooltip"
                    className={cn(
                        'absolute z-50 px-3 py-2 text-sm text-background bg-foreground rounded-lg shadow-lg',
                        'animate-in fade-in-0 zoom-in-95 duration-150',
                        positionClasses[adjustedPosition],
                        maxWidthClass
                    )}
                >
                    {content}
                    {/* Arrow */}
                    <div
                        className={cn(
                            'absolute w-0 h-0 border-4',
                            arrowClasses[adjustedPosition]
                        )}
                    />
                </div>
            )}
        </div>
    );
};

export default Tooltip;
